import { useEffect, useMemo, useState } from "react";
import {
  Banner,
  Button,
  Card,
  CardGrid,
  Cell,
  FormItem,
  Group,
  Header,
  Input,
  Select,
  Text
} from "@vkontakte/vkui";
import { Icon24ExternalLinkOutline, Icon24LocationMapOutline, Icon24Search } from "@vkontakte/icons";
import type { Partner } from "../api/types";
import { api, errorMessage } from "../api/client";
import { formatDate, formatMoney } from "../shared/format";
import { Page } from "../components/Page";
import { EmptyState, ErrorState, LoadingState } from "../components/States";
import { PartnerMedia } from "../components/PartnerMedia";

function isCurrentlyActive(rule: { isActive: boolean; startsAt?: string | null; endsAt?: string | null }) {
  const now = Date.now();
  const startsAt = rule.startsAt ? new Date(rule.startsAt).getTime() : null;
  const endsAt = rule.endsAt ? new Date(rule.endsAt).getTime() : null;
  return rule.isActive && (!startsAt || startsAt <= now) && (!endsAt || endsAt >= now);
}

export function PartnersPage({ navigate }: { navigate: (path: string) => void }) {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    api
      .get<{ partners: Partner[] }>("/partners")
      .then((response) => {
        setPartners(response.data.partners);
        setError("");
      })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ["all", ...new Set(partners.map((partner) => partner.category))], [partners]);
  const filtered = partners.filter((partner) => {
    const matchesQuery = `${partner.title} ${partner.description}`.toLowerCase().includes(query.toLowerCase());
    const matchesCategory = category === "all" || partner.category === category;
    return matchesQuery && matchesCategory;
  });

  return (
    <Page title="Партнёры">
      <Group>
        <FormItem top="Поиск">
          <Input before={<Icon24Search />} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Название или описание" />
        </FormItem>
        <FormItem top="Категория">
          <Select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            options={categories.map((item) => ({ label: item === "all" ? "Все категории" : item, value: item }))}
          />
        </FormItem>
      </Group>

      {loading ? <LoadingState /> : null}
      {error ? <ErrorState message={error} /> : null}
      {!loading && !error && filtered.length === 0 ? <EmptyState text="Попробуйте изменить запрос или категорию." /> : null}

      <Group header={<Header>Каталог</Header>}>
        <CardGrid size="l">
          {filtered.map((partner) => (
            <Card key={partner.id} mode="shadow" onClick={() => navigate(`/partners/${partner.id}`)}>
              <PartnerMedia title={partner.title} category={partner.category} logoUrl={partner.logoUrl} coverUrl={partner.coverUrl} />
              <Cell
                subtitle={`${partner.category} · ${partner.discountBenefitLabel || "специальные условия"}`}
                after={partner.hasActiveOffer ? "Акция" : partner.isPromoted ? "Продвигается" : undefined}
              >
                {partner.title}
              </Cell>
            </Card>
          ))}
        </CardGrid>
      </Group>
    </Page>
  );
}

export function PartnerDetailPage({ id, navigate }: { id: string; navigate: (path: string) => void }) {
  const [partner, setPartner] = useState<Partner | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .get<{ partner: Partner }>(`/partners/${id}`)
      .then((response) => {
        setPartner(response.data.partner);
        setError("");
        return api.post(`/partners/${id}/view`);
      })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Page title="Партнёр">
        <LoadingState />
      </Page>
    );
  }

  if (error || !partner) {
    return (
      <Page title="Партнёр">
        <ErrorState message={error || "Партнёр не найден"} />
      </Page>
    );
  }

  const visiblePercentRules = (partner.discounts || []).filter((rule) => isCurrentlyActive(rule) && rule.discountPercent > 0);
  const visibleRangeRules = (partner.discountRanges || []).filter((rule) => isCurrentlyActive(rule) && rule.discountAmount > 0);

  return (
    <Page title={partner.title}>
      <Group>
        <PartnerMedia
          title={partner.title}
          category={partner.category}
          logoUrl={partner.logoUrl}
          coverUrl={partner.coverUrl}
          variant="detail"
        />
        <Text>{partner.description}</Text>
        <div className="toolbar" style={{ marginTop: 12 }}>
          {partner.address ? (
            <Button before={<Icon24LocationMapOutline />} mode="secondary" href={`https://yandex.ru/maps/?text=${encodeURIComponent(partner.address)}`} target="_blank">
              Маршрут
            </Button>
          ) : null}
          {partner.websiteUrl ? (
            <Button before={<Icon24ExternalLinkOutline />} mode="secondary" href={partner.websiteUrl} target="_blank">
              Сайт
            </Button>
          ) : null}
          <Button mode="tertiary" onClick={() => navigate("/partners")}>
            Назад
          </Button>
        </div>
      </Group>

      <Group header={<Header>Условия скидок</Header>}>
        {visiblePercentRules.length || visibleRangeRules.length ? null : <EmptyState text="Активные скидочные условия пока не опубликованы." />}
        {visiblePercentRules.map((rule) => (
          <Cell
            key={rule.membershipLevel}
            subtitle={`${rule.conditionsText}${rule.minPurchaseAmount ? ` · от ${formatMoney(rule.minPurchaseAmount)}` : ""}`}
            after={`${rule.discountPercent}%`}
          >
            {rule.membershipLevel}
          </Cell>
        ))}
        {visibleRangeRules.length ? (
          visibleRangeRules.map((rule) => (
            <Cell
              key={rule.id}
              subtitle={`${rule.conditionsText} · от ${formatMoney(rule.minPurchaseAmount)}${rule.maxPurchaseAmount ? ` до ${formatMoney(rule.maxPurchaseAmount)}` : ""}`}
              after={formatMoney(rule.discountAmount)}
            >
              {rule.title}
            </Cell>
          ))
        ) : null}
      </Group>

      <Group header={<Header>Акции</Header>}>
        {partner.offers?.length ? (
          partner.offers.map((offer) => (
            <Banner key={offer.id} header={offer.title} subheader={`${offer.description} · ${offer.discountText}`} />
          ))
        ) : (
          <EmptyState text="У партнёра пока нет активных акций." />
        )}
      </Group>

      <Group header={<Header>Контакты</Header>}>
        <Cell subtitle="Адрес">{partner.address || "Не указан"}</Cell>
        <Cell subtitle="Телефон">{partner.phone || "Не указан"}</Cell>
        <Cell subtitle="VK">{partner.vkUrl || "Не указан"}</Cell>
        <Cell subtitle="Просмотры">{String(partner.viewsCount)}</Cell>
        {partner.promotedUntil ? <Cell subtitle="Продвижение до">{formatDate(partner.promotedUntil)}</Cell> : null}
      </Group>
    </Page>
  );
}
